import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";

/**
 * Workshop: Agent list dropdown — shows agents with status dots.
 */

type AgentStatus = "active" | "idle" | "offline";

interface AgentListItem {
  id: string;
  name: string;
  color: string;
  status: AgentStatus;
}

interface AgentListProps {
  agents: AgentListItem[];
  selectedId?: string;
  onSelect?: (agentId: string) => void;
  onAdd?: () => void;
  className?: string;
  "data-testid"?: string;
}

const statusColorMap: Record<AgentStatus, string> = {
  active: "bg-accent",
  idle: "bg-warning",
  offline: "bg-muted",
};

export function AgentList({
  agents,
  selectedId,
  onSelect,
  onAdd,
  className,
  "data-testid": testId = "agent-list",
}: AgentListProps) {
  return (
    <div
      className={cn(
        "flex w-60 flex-col rounded-xl border border-glass-stroke bg-glass-fill shadow-glass backdrop-blur-glass-md",
        className,
      )}
      data-testid={testId}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 pb-1.5 pt-2">
        <span className="text-[9px] font-medium uppercase tracking-wider text-muted">
          // Agents
        </span>
        <button
          type="button"
          onClick={onAdd}
          className="flex h-5 w-5 items-center justify-center rounded text-muted transition-colors hover:text-primary"
          data-testid={`${testId}-add`}
          aria-label="Add agent"
        >
          <Icons.Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Agent rows */}
      <div className="flex flex-col gap-0.5 px-3 pb-2">
        {agents.length === 0 && (
          <p className="py-3 text-center text-[10px] text-muted">
            No agents in workspace
          </p>
        )}

        {agents.map((agent) => {
          const isSelected = agent.id === selectedId;

          return (
            <button
              key={agent.id}
              type="button"
              onClick={() => onSelect?.(agent.id)}
              className={cn(
                "flex items-center gap-2 rounded px-1.5 py-1 text-left transition-colors hover:bg-glass-fill-heavy",
              )}
              style={
                isSelected
                  ? { backgroundColor: `${agent.color}20` }
                  : undefined
              }
              data-testid={`${testId}-item-${agent.id}`}
            >
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[8px] font-semibold text-white"
                style={{ backgroundColor: agent.color }}
                aria-hidden="true"
              >
                {agent.name.charAt(0).toUpperCase()}
              </span>
              <span className="flex-1 truncate text-[10px] text-secondary">
                {agent.name}
              </span>
              <span
                className={cn(
                  "h-2 w-2 shrink-0 rounded-full",
                  statusColorMap[agent.status],
                )}
                aria-label={agent.status}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export type { AgentListProps, AgentListItem, AgentStatus as AgentListStatus };
